import { useRef, useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import TurnstileWidget, { type TurnstileWidgetHandle } from '@/components/feature/TurnstileWidget';
import { verifyTurnstileToken } from '@/lib/turnstile';

export default function NewsletterForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('idle');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setEmail('');
      form.reset();
      return;
    }

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setErrorMsg(t('blog.invalidEmail'));
      return;
    }

    if (!turnstileToken) {
      setStatus('error');
      setErrorMsg(t('blog.captchaRequired'));
      return;
    }

    const captchaOk = await verifyTurnstileToken(turnstileToken);
    if (!captchaOk) {
      setStatus('error');
      setErrorMsg(t('blog.captchaFailed'));
      turnstileRef.current?.reset();
      setTurnstileToken(null);
      return;
    }

    setLoading(true);
    try {
      const payload = new URLSearchParams();
      payload.append('email', email);
      const response = await fetch('https://readdy.ai/api/form/da0t5u6859p3u981e6h0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        (parsed as Record<string, unknown>)?.meta?.message ||
        (parsed as Record<string, unknown>)?.message ||
        (parsed as Record<string, unknown>)?.meta?.detail ||
        responseText;
      const code = (parsed as Record<string, unknown>)?.code;

      if (response.ok && code === 'OK') {
        setStatus('success');
        setEmail('');
        form.reset();
        turnstileRef.current?.reset();
        setTurnstileToken(null);
      } else {
        setStatus('error');
        setErrorMsg(typeof serverMsg === 'string' ? serverMsg : t('blog.errorGeneric'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('blog.errorNetwork'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 lg:px-6 pb-10 lg:pb-[100px]">
      <div className="flex flex-col items-start justify-between gap-6 rounded-[20px] bg-primary-100 px-6 py-9 sm:flex-row sm:items-center lg:px-13 lg:py-12">
        <div className="max-w-[52ch]">
          <h3 className="text-[22px] font-extrabold tracking-[-0.03em] text-secondary-900 lg:text-[26px]">
            {t('blog.newsTitle')}
          </h3>
          <p className="mt-2 text-[14px] leading-[1.6] text-secondary-600 lg:text-[15px]">
            {t('blog.newsText')}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form="da0t5u6859p3u981e6h0"
          className="flex w-full flex-col gap-3 sm:w-auto sm:items-start"
        >
          {/* Honeypot */}
          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="vts-field-check"
          />

          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1 sm:w-[280px]">
              <input
                type="email"
                name="email"
                placeholder={t('blog.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-[50px] w-full rounded-full border border-secondary-200 bg-white pl-5 pr-4 text-[14px] text-secondary-900 placeholder:text-secondary-400 outline-none transition-colors focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3.5 text-[14px] font-bold text-white transition-colors hover:bg-primary-600 disabled:opacity-60 whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-sm" />
                  {t('blog.subscribing')}
                </>
              ) : (
                <>
                  {t('blog.subscribe')}
                  <i className="ri-arrow-right-line text-sm" />
                </>
              )}
            </button>
          </div>

          <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} />
        </form>
      </div>

      {status === 'success' && (
        <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-[13px] text-emerald-700 flex items-center gap-2">
          <i className="ri-check-line" />
          {t('blog.thanks')}
        </div>
      )}
      {status === 'error' && (
        <div className="mt-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line" />
          {errorMsg}
        </div>
      )}
    </section>
  );
}